import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState, useSetRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import {
  addStateLineOfBusiness,
  editStateLineOfBusiness,
  lobSaving,
  rowForEditLineOfBusiness,
} from "../atoms/atoms";
import useAddLineOfBusiness from "../hooks/useAddLineOfBusiness";
import useUpdateLineOfBusiness from "../hooks/useUpdateLineOfBusiness";
import { lineOfBusinessInfo } from "../types";
import LineOfBusinessDetails from "./LineOfBusinessDetails";
import Timer from "../../Timer";

export const LineOfBusinessForm = (props: { refetch: () => void }) => {
  // atoms
  const [editData, setEditData] = useRecoilState(rowForEditLineOfBusiness);
  const [isSaving, setIsSaving] = useRecoilState(lobSaving);
  const [isAddState, setIsAddState] = useRecoilState(addStateLineOfBusiness);
  const [isEditState, setIsEditState] = useRecoilState(editStateLineOfBusiness);

  // hooks
  const { refetch } = props;
  const addLineOfBusiness = useAddLineOfBusiness();
  const updateLineOfBusiness = useUpdateLineOfBusiness();

  // local
  const [newData, setNewData] = useState<lineOfBusinessInfo>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  //Edit Business License & Permit Initialization/

  // form methods
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      lineOfBusinessName: data.lineOfBusinessName?.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (isEditState) {
        updateLineOfBusiness(newData);
      } else {
        addLineOfBusiness(newData);
      }
    }
    await Timer();
    setIsSaving(false);
    refetch();
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  return (
    <>
      <div className="p-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState
                  ? "Add New Line of Business"
                  : "Edit Line of Business"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <LineOfBusinessDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={
            isAddState ? "Add Line of Business" : "Edit Line of Business"
          }
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this data?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            handleSave();
            setIsSaving(false);
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
};
